/**
 * Predicates for the key and value patterns of an OVD file. They accept `unknown`
 * because they run on raw JSON.
 *
 * No dependency on `@tedivo/tedivo-bay-grid-pure`: candidate to move into
 * `open-vessel-definition`.
 */
import { BayLevelEnum, CONTAINER_LENGTHS, TContainerLengths } from "open-vessel-definition";

import { OVD_BOUNDS } from "./consts";

const BAY_RE = /^\d{3}$/;
const ROW_RE = /^\d{2}$/;
const TIER_RE = /^\d{2,3}$/;
const SLOT_RE = /^(\d{2})(\d{2,3})$/;
const BAY_RANGE_RE = /^(\d{3})-(\d{3})$/;

export function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

export function isYesNo(v: unknown): v is 0 | 1 {
  return v === 0 || v === 1;
}

export function isForeAft(v: unknown): v is 1 | 2 {
  return v === 1 || v === 2;
}

/** Older files write a `ForeAftEnum` field that is not set as `0` or `null` instead of leaving it out */
export function isUnsetEnum(v: unknown): v is 0 | null {
  return v === 0 || v === null;
}

/** 3 digits, odd, within bounds */
export function isIsoBayKey(s: unknown): s is string {
  if (typeof s !== "string" || !BAY_RE.test(s)) return false;
  const n = Number(s);
  return n % 2 === 1 && n >= OVD_BOUNDS.BAY_MIN && n < OVD_BOUNDS.BAY_MAX_EXCLUSIVE;
}

/** 3 digits and odd, whatever the bounds */
export function isOddIsoBayString(s: unknown): s is string {
  return typeof s === "string" && BAY_RE.test(s) && Number(s) % 2 === 1;
}

/** 2 digits, within bounds */
export function isRowKey(s: unknown): s is string {
  return typeof s === "string" && ROW_RE.test(s) && Number(s) <= OVD_BOUNDS.ROW_MAX;
}

/** 2 or 3 digits, even, within the overall tier bounds */
export function isTierKey(s: unknown): s is string {
  if (typeof s !== "string" || !TIER_RE.test(s)) return false;
  const n = Number(s);
  return n % 2 === 0 && n >= OVD_BOUNDS.BELOW_TIER_MIN && n <= OVD_BOUNDS.ABOVE_TIER_MAX;
}

export function isAboveTier(tier: number): boolean {
  return tier >= OVD_BOUNDS.ABOVE_TIER_MIN;
}

/** Whether a tier number belongs to the level's range. Levels other than ABOVE/BELOW are not judged */
export function tierWithinLevel(tier: number, level: unknown): boolean {
  if (level === BayLevelEnum.ABOVE)
    return tier >= OVD_BOUNDS.ABOVE_TIER_MIN && tier <= OVD_BOUNDS.ABOVE_TIER_MAX;
  if (level === BayLevelEnum.BELOW)
    return tier >= OVD_BOUNDS.BELOW_TIER_MIN && tier <= OVD_BOUNDS.BELOW_TIER_MAX;
  return true;
}

export interface IParsedSlotKey {
  row: string;
  tier: string;
  rowN: number;
  tierN: number;
}

/** Splits a slot key (`RRTT` or `RRTTT`) into row and tier. `undefined` when it is not one */
export function parseSlotKey(key: unknown): IParsedSlotKey | undefined {
  if (typeof key !== "string") return undefined;
  const m = SLOT_RE.exec(key);
  if (m === null) return undefined;
  return { row: m[1], tier: m[2], rowN: Number(m[1]), tierN: Number(m[2]) };
}

export function isContainerLength(n: unknown): n is TContainerLengths {
  return typeof n === "number" && (CONTAINER_LENGTHS as number[]).includes(n);
}

/** A container-length dictionary key, e.g. `"40"` */
export function isContainerLengthKey(s: unknown): boolean {
  return typeof s === "string" && /^\d+$/.test(s) && isContainerLength(Number(s));
}

/** `"049-051"`: two valid ISO bays */
export function parseIsoBayRange(s: unknown): [string, string] | undefined {
  if (typeof s !== "string") return undefined;
  const m = BAY_RANGE_RE.exec(s);
  if (m === null || !isIsoBayKey(m[1]) || !isIsoBayKey(m[2])) return undefined;
  return [m[1], m[2]];
}

/** IMO number: 7 digits whose last one is the check digit of the first six */
export function imoCheckDigitOk(code: unknown): boolean {
  if (typeof code !== "string" || !/^\d{7}$/.test(code)) return false;
  let sum = 0;
  for (let i = 0; i < 6; i++) sum += Number(code[i]) * (7 - i);
  return sum % 10 === Number(code[6]);
}
